import type {ReactElement} from 'react';
import React, {useEffect, useRef} from 'react';
import styles from './wavy-text.module.scss';
import {Text} from '@Components/text';
import type {WavyTextProps} from './wavy-text.types';
import {ColorType} from './wavy-text.types';

export function WavyText({val, containerClassName, colorType = ColorType.DEFAULT, ...props}: WavyTextProps): ReactElement<any> {
  const containerRef = useRef<HTMLDivElement>(null);

  const applyBackgroundOffsets = (): void => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    const allSpans = container.querySelectorAll<HTMLSpanElement>('span');
    const totalWidth = container.scrollWidth;

    allSpans.forEach((span, i) => {
      const offsetLeft = span.offsetLeft;
      span.style.backgroundSize = `${totalWidth}px 100%`;
      span.style.backgroundPosition = `-${offsetLeft}px 0`;
      span.style.animationDelay = `${i * 0.07}s`;
    });
  };

  useEffect(() => {
    if (!containerRef.current) return;

    applyBackgroundOffsets();

    const resizeObserver = new ResizeObserver(() => {
      applyBackgroundOffsets();
    });
    resizeObserver.observe(containerRef.current);

    return (): void => {
      resizeObserver.disconnect();
    };
  }, [val]);

  const getTextColorBasedOnSeason = (): string => {
    switch (colorType) {
      case ColorType.DEFAULT:
        return styles.neutralColor;
      case ColorType.HALLOWEEN:
        return styles.halloweenColor;
      case ColorType.CHRISTMAS:
        return styles.christmasColor;
      default:
        return '';
    }
  };

  const getEmoji = (): ReactElement<any> | null => {
    if (!props.emojiText) {
      return null;
    }

    return (
      <>
        <Text className={`js-wavy-text-char ${styles.wavyGradientTextSpan}`} val={props.emojiText} {...props} />{' '}
      </>
    );
  };

  return (
    <div ref={containerRef} className={`js-wavy-text-container _position-relative  ${containerClassName}`} data-text={val}>
      {props.emojiText ? getEmoji() : null}

      {val.split('').map((char, i) => {
        return (
          <Text
            key={`${i}-${char}`}
            className={`js-wavy-text-char ${getTextColorBasedOnSeason()} ${styles.wavyGradientTextSpan} ${styles.transparentColor}`}
            val={char === ' ' ? '\u00A0' : char}
            {...props}
          />
        );
      })}
    </div>
  );
}
